import { useMutation, useQuery, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import {
  reactQueryGet,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

const SPACES_INDEX_QUERY_KEY = ["internal_api", "community_bot", "spaces"];
const SETTING_SHOW_QUERY_KEY = ["internal_api", "community_bot", "setting"];

interface SpaceProp {
  created_at: string;
  id: number;
  name: string;
  posts_count: number;
  space_group: string;
  updated_at: string;
}

interface SpaceResponse {
  count: number;
  records: Array<SpaceProp>;
}

interface SettingResponse {
  agents_limit: number;
  hide_agents_banner: boolean;
  hide_knowledge_banner: boolean;
  hide_preview_banner: boolean;
  id: number;
}

interface ApiUpdateSettingResponse {
  agents_limit: number;
  hide_agents_banner: boolean;
  hide_knowledge_banner: boolean;
  hide_preview_banner: boolean;
}

interface UpdateSettingRequest {
  hide_agents_banner?: boolean;
  hide_knowledge_banner?: boolean;
  hide_preview_banner?: boolean;
}

interface ApiUpdateSettingRequest {
  community_bot_setting: {
    hide_knowledge_banner?: boolean;
    hide_agents_banner?: boolean;
    hide_preview_banner?: boolean;
  };
}

export const useIndexSpaces = ({
  currentPage,
  name,
  type,
  spaceGroup,
}: {
  currentPage: number;
  name?: string;
  type?: string;
  spaceGroup?: string;
}) =>
  useQuery<SpaceResponse>({
    queryKey: [SPACES_INDEX_QUERY_KEY, currentPage, name, type, spaceGroup],
    queryFn: () =>
      reactQueryGet(
        internalApi.communityBot.spaces.index({
          params: {
            page: currentPage,
            per_page: 10,
            ...(name ? { name } : {}),
            ...(type ? { type } : {}),
            ...(spaceGroup ? { space_group: spaceGroup } : {}),
          },
        }),
      ),
  });

export const useShowSetting = () =>
  useQuery<SettingResponse>({
    queryKey: SETTING_SHOW_QUERY_KEY,
    queryFn: () => reactQueryGet(internalApi.communityBot.setting.show()),
  });

export const useUpdateSetting = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation<ApiUpdateSettingResponse, Error, UpdateSettingRequest>(
    (data: UpdateSettingRequest) => {
      const apiData: ApiUpdateSettingRequest = {
        community_bot_setting: {
          ...data,
        },
      };
      return reactQueryPut(internalApi.communityBot.setting.update(), apiData);
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(SETTING_SHOW_QUERY_KEY);
        void queryClient.invalidateQueries(SPACES_INDEX_QUERY_KEY);

        success(t("changes_saved"));
      },
      onError: () => {
        error(t("request_failure_message"));
      },
    },
  );
};
